/**
 * CreateOrderModal - Modal for creating new admin orders
 * 
 * Features:
 * - Customer picker (search/select from existing) - indexed for 1000s of records
 * - Fulfillment method selector (SHIP/PICKUP)
 * - Ship-To contact picker (for SHIP orders)
 * - Order summary field (above line items)
 * - Order-level email field
 * - Line items with separate item name and multi-line description
 * - Discount with type selector (flat/percentage) - consistent with Order Detail
 * - Notes field
 * - Artwork required toggle
 * - Prefill support for customer context
 * 
 * HARDENING (P1.1):
 * - prefillCustomer.id (accountId) is SOURCE OF TRUTH for API submission
 * - Display fields (name, email, phone) are for UI only
 * - On submit, account_id links order to customer record
 */
import React, { useState, useEffect, useCallback } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Switch } from '../ui/switch';
import { Badge } from '../ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../ui/alert-dialog';
import { crmAPI, adminAPI } from '../../utils/api';
import { toast } from 'sonner';
import {
  Search,
  Loader2,
  Building2,
  X,
  Plus,
  Trash2,
  Truck,
  Store,
  MapPin,
  Star,
  AlertTriangle,
  Mail,
  ChevronDown,
  ChevronUp,
  Percent,
  DollarSign,
  Copy,
} from 'lucide-react';

const CreateOrderModal = ({
  open,
  onOpenChange,
  prefillCustomer = null, // { id (SOURCE OF TRUTH), name, email, phone (display only) }
  prefillFromOrderIds = null, // Array of order IDs to copy from (for reorder/combine)
  onSuccess,
}) => {
  // Prefill from orders state
  const [prefillLoading, setPrefillLoading] = useState(false);
  const [prefillSourceOrders, setPrefillSourceOrders] = useState([]);
  
  // Customer selection - selectedCustomer.id is the SOURCE OF TRUTH
  const [customerSearch, setCustomerSearch] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [searchLoading, setSearchLoading] = useState(false);
  const [selectedCustomer, setSelectedCustomer] = useState(null); // { id, name, email, phone }
  const [showCustomerPicker, setShowCustomerPicker] = useState(false);

  // Form data - display values (may differ from selectedCustomer for manual entry)
  const [customerName, setCustomerName] = useState('');
  const [customerEmail, setCustomerEmail] = useState('');
  const [customerPhone, setCustomerPhone] = useState('');
  const [customerPoNumber, setCustomerPoNumber] = useState(''); // Customer's PO/SO reference
  const [orderEmail, setOrderEmail] = useState(''); // Order-level email for notifications
  const [notes, setNotes] = useState('');
  const [artworkRequired, setArtworkRequired] = useState(false);
  const [lineItems, setLineItems] = useState([]);
  
  // Pricing fields
  const [shippingCost, setShippingCost] = useState('');
  const [discountType, setDiscountType] = useState('flat'); // 'flat' | 'percentage'
  const [discountValue, setDiscountValue] = useState('');
  const [discountDescription, setDiscountDescription] = useState('');
  const [orderSummary, setOrderSummary] = useState('');

  // Ship-To / Fulfillment
  const [fulfillmentMethod, setFulfillmentMethod] = useState('SHIP'); // SHIP | PICKUP
  const [shipToContacts, setShipToContacts] = useState([]);
  const [selectedShipToContact, setSelectedShipToContact] = useState(null);
  const [loadingShipToContacts, setLoadingShipToContacts] = useState(false);
  const [showNewShipToModal, setShowNewShipToModal] = useState(false);
  const [showDefaultChangeWarning, setShowDefaultChangeWarning] = useState(false);
  const [pendingDefaultContact, setPendingDefaultContact] = useState(null);
  
  // New Ship-To Contact form
  const [newShipToForm, setNewShipToForm] = useState({
    first_name: '',
    last_name: '',
    email: '',
    phone: '',
    shipping_address: {
      address_line1: '',
      address_line2: '',
      city: '',
      state: '',
      postal_code: '',
      country: 'USA',
    },
    delivery_notes: '',
    is_default_ship_to: false,
  });
  const [savingShipTo, setSavingShipTo] = useState(false);
  
  // Missing address fix modal
  const [showMissingAddressModal, setShowMissingAddressModal] = useState(false);
  const [missingAddressContact, setMissingAddressContact] = useState(null); // { contact_id, contact_name }
  const [missingAddressForm, setMissingAddressForm] = useState({
    address_line1: '',
    address_line2: '',
    city: '',
    state: '',
    postal_code: '',
    country: 'USA',
  });
  const [savingMissingAddress, setSavingMissingAddress] = useState(false);

  // Submission
  const [submitting, setSubmitting] = useState(false);

  // Initialize with prefill data
  useEffect(() => {
    if (open && prefillCustomer) {
      // Store the full customer object (id is source of truth)
      setSelectedCustomer(prefillCustomer);
      // Populate display fields
      setCustomerName(prefillCustomer.name || prefillCustomer.company_name || '');
      setCustomerEmail(prefillCustomer.email || '');
      setCustomerPhone(prefillCustomer.phone || '');
      setShowCustomerPicker(false);
    } else if (open && !prefillCustomer && !prefillFromOrderIds) {
      setShowCustomerPicker(true);
    }
  }, [open, prefillCustomer, prefillFromOrderIds]);

  // Prefill from existing orders (reorder/combine feature)
  useEffect(() => {
    const fetchOrdersForPrefill = async () => {
      if (!open || !prefillFromOrderIds || prefillFromOrderIds.length === 0) {
        setPrefillSourceOrders([]);
        return;
      }
      
      setPrefillLoading(true);
      try {
        const response = await adminAPI.getCombineOrdersPreview(prefillFromOrderIds);
        
        if (response.success) {
          // Set customer info
          const customer = response.customer;
          setSelectedCustomer({
            id: customer.account_id,
            name: customer.name,
            email: customer.email,
            phone: customer.phone,
          });
          setCustomerName(customer.name || '');
          setCustomerEmail(customer.email || '');
          setCustomerPhone(customer.phone || '');
          setShowCustomerPicker(false);
          
          // Set line items - convert to local format
          const items = response.items.map((item, idx) => ({
            id: `prefill-${idx}`,
            product_name: item.product_name || '',
            description: item.description || '',
            quantity: item.quantity || 1,
            unit_price: item.unit_price || 0,
            product_id: item.product_id,
            source_orders: item.source_orders || [],
          }));
          setLineItems(items);
          
          // Set other prefill fields
          if (response.po_number) setCustomerPoNumber(response.po_number);
          if (response.notes) setNotes(response.notes);
          if (response.order_summary) setOrderSummary(response.order_summary);
          
          // Store source orders for display
          setPrefillSourceOrders(response.source_order_ids || []);
          
          toast.success(`Loaded ${items.length} items from ${response.orders_combined_count} order(s)`);
        }
      } catch (err) {
        console.error('Failed to prefill from orders:', err);
        toast.error(err.message || 'Failed to load order data');
        setPrefillSourceOrders([]);
      } finally {
        setPrefillLoading(false);
      }
    };
    
    fetchOrdersForPrefill();
  }, [open, prefillFromOrderIds]);

  // Reset form when closed
  useEffect(() => {
    if (!open) {
      setCustomerSearch('');
      setSearchResults([]);
      setSelectedCustomer(null);
      setCustomerName('');
      setCustomerEmail('');
      setCustomerPhone('');
      setCustomerPoNumber('');
      setOrderEmail('');
      setNotes('');
      setArtworkRequired(false);
      setLineItems([]);
      // Reset pricing fields
      setShippingCost('');
      setDiscountType('flat');
      setDiscountValue('');
      setDiscountDescription('');
      setOrderSummary('');
      setShowCustomerPicker(false);
      // Reset Ship-To
      setFulfillmentMethod('SHIP');
      setShipToContacts([]);
      setSelectedShipToContact(null);
      setNewShipToForm({
        first_name: '',
        last_name: '',
        email: '',
        phone: '',
        shipping_address: {
          address_line1: '',
          address_line2: '',
          city: '',
          state: '',
          postal_code: '',
          country: 'USA',
        },
        delivery_notes: '',
        is_default_ship_to: false,
      });
      // Reset prefill state
      setPrefillSourceOrders([]);
      setPrefillLoading(false);
    }
  }, [open]);

  // Fetch ship-to contacts when customer is selected
  useEffect(() => {
    if (selectedCustomer?.id && fulfillmentMethod === 'SHIP') {
      fetchShipToContacts(selectedCustomer.id);
    } else {
      setShipToContacts([]);
      setSelectedShipToContact(null);
    }
  }, [selectedCustomer?.id, fulfillmentMethod]);

  const fetchShipToContacts = async (customerId) => {
    setLoadingShipToContacts(true);
    try {
      const res = await crmAPI.getCustomerContactsForShipTo(customerId);
      const contacts = res.contacts || [];
      setShipToContacts(contacts);
      
      // Auto-select default ship-to contact
      const defaultContact = contacts.find(c => c.is_default_ship_to);
      if (defaultContact) {
        setSelectedShipToContact(defaultContact);
      } else if (contacts.length > 0 && contacts[0].shipping_address) {
        // Select first contact with shipping address
        setSelectedShipToContact(contacts[0]);
      }
    } catch (err) {
      console.error('Failed to fetch ship-to contacts:', err);
    } finally {
      setLoadingShipToContacts(false);
    }
  };

  // Customer search with debounce
  useEffect(() => {
    if (!showCustomerPicker) return;
    if (customerSearch.length < 2) {
      setSearchResults([]);
      return;
    }

    const timer = setTimeout(async () => {
      setSearchLoading(true);
      try {
        const res = await crmAPI.getAccounts({ q: customerSearch, page_size: 10 });
        setSearchResults(res.accounts || res.items || []);
      } catch (err) {
        console.error('Customer search failed:', err);
      } finally {
        setSearchLoading(false);
      }
    }, 300);

    return () => clearTimeout(timer);
  }, [customerSearch, showCustomerPicker]);

  const handleSelectCustomer = (customer) => {
    setSelectedCustomer(customer);
    setCustomerName(customer.name || customer.company_name || '');
    setCustomerEmail(customer.email || '');
    setCustomerPhone(customer.phone || '');
    setShowCustomerPicker(false);
    setCustomerSearch('');
    setSearchResults([]);
  };

  const handleClearCustomer = () => {
    setSelectedCustomer(null);
    setCustomerName('');
    setCustomerEmail('');
    setCustomerPhone('');
    setShowCustomerPicker(true);
  };

  const addLineItem = () => {
    setLineItems([
      ...lineItems,
      { 
        id: Date.now(), 
        item_name: '',      // Product/Item name (single line)
        description: '',    // Multi-line description for specs/details
        quantity: 1, 
        unit_price: 0 
      },
    ]);
  };

  const updateLineItem = (id, field, value) => {
    setLineItems(lineItems.map((item) =>
      item.id === id ? { ...item, [field]: value } : item
    ));
  };

  const removeLineItem = (id) => {
    setLineItems(lineItems.filter((item) => item.id !== id));
  };

  const calculateSubtotal = () => {
    return lineItems.reduce((sum, item) => sum + (item.quantity * item.unit_price), 0);
  };
  
  // Calculate discount amount based on type
  const calculateDiscountAmount = useCallback(() => {
    const subtotal = lineItems.reduce((sum, item) => sum + (item.quantity * item.unit_price), 0);
    const value = parseFloat(discountValue) || 0;
    if (discountType === 'percentage') {
      return (subtotal * value / 100);
    }
    return value;
  }, [lineItems, discountType, discountValue]);
  
  const calculateTotal = () => {
    const subtotal = calculateSubtotal();
    const shipping = parseFloat(shippingCost) || 0;
    const discount = calculateDiscountAmount();
    return Math.max(0, subtotal + shipping - discount);
  };

  // Create new ship-to contact
  const handleCreateShipToContact = async () => {
    // Validate required fields
    if (!newShipToForm.first_name && !newShipToForm.last_name) {
      toast.error('Contact name is required');
      return;
    }
    if (!newShipToForm.shipping_address.address_line1) {
      toast.error('Street address is required');
      return;
    }
    if (!newShipToForm.shipping_address.city || !newShipToForm.shipping_address.state || !newShipToForm.shipping_address.postal_code) {
      toast.error('City, state, and postal code are required');
      return;
    }

    // Check if setting as default when one already exists
    const currentDefault = shipToContacts.find(c => c.is_default_ship_to);
    if (newShipToForm.is_default_ship_to && currentDefault) {
      setPendingDefaultContact({ ...newShipToForm, isNew: true });
      setShowDefaultChangeWarning(true);
      return;
    }

    await saveNewShipToContact();
  };

  const saveNewShipToContact = async () => {
    setSavingShipTo(true);
    try {
      const contactData = {
        account_id: selectedCustomer.id,
        first_name: newShipToForm.first_name,
        last_name: newShipToForm.last_name,
        email: newShipToForm.email || null,
        phone: newShipToForm.phone || null,
        shipping_address: newShipToForm.shipping_address,
        delivery_notes: newShipToForm.delivery_notes || null,
        is_default_ship_to: newShipToForm.is_default_ship_to,
      };

      const result = await crmAPI.createContact(contactData);
      toast.success('Ship-to contact created');
      setShowNewShipToModal(false);
      
      // Refresh contacts and select the new one
      await fetchShipToContacts(selectedCustomer.id);
      setSelectedShipToContact(result.contact);
      
      // Reset form
      setNewShipToForm({
        first_name: '',
        last_name: '',
        email: '',
        phone: '',
        shipping_address: {
          address_line1: '',
          address_line2: '',
          city: '',
          state: '',
          postal_code: '',
          country: 'USA',
        },
        delivery_notes: '',
        is_default_ship_to: false,
      });
    } catch (err) {
      toast.error(err.message || 'Failed to create contact');
    } finally {
      setSavingShipTo(false);
      setShowDefaultChangeWarning(false);
      setPendingDefaultContact(null);
    }
  };

  // Handle setting existing contact as default
  const handleSetDefaultShipTo = async (contact) => {
    const currentDefault = shipToContacts.find(c => c.is_default_ship_to);
    if (currentDefault && currentDefault.id !== contact.id) {
      setPendingDefaultContact(contact);
      setShowDefaultChangeWarning(true);
    } else {
      await confirmSetDefaultShipTo(contact);
    }
  };

  const confirmSetDefaultShipTo = async (contact) => {
    try {
      await crmAPI.setDefaultShipTo(contact.id);
      toast.success('Default ship-to updated');
      await fetchShipToContacts(selectedCustomer.id);
    } catch (err) {
      toast.error(err.message || 'Failed to set default');
    } finally {
      setShowDefaultChangeWarning(false);
      setPendingDefaultContact(null);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!customerName.trim()) {
      toast.error('Customer name is required');
      return;
    }

    // Validate ship-to for SHIP orders
    if (fulfillmentMethod === 'SHIP' && selectedCustomer?.id && !selectedShipToContact) {
      toast.error('Please select a ship-to contact for shipping orders');
      return;
    }

    setSubmitting(true);
    try {
      // HARDENING: account_id (from selectedCustomer.id) is SOURCE OF TRUTH
      // Display fields are sent for reference but backend should use account_id
      // to fetch fresh customer data if needed
      const orderData = {
        // SOURCE OF TRUTH - links order to CRM account
        account_id: selectedCustomer?.id || null,
        // Display values - for order record, not for customer lookup
        customer_name: customerName,
        customer_phone: customerPhone || null,
        // Customer PO/SO reference
        customer_po_number: customerPoNumber || null,
        notes: notes || null,
        artwork_required: artworkRequired,
        // Fulfillment & Ship-To
        fulfillment_method: fulfillmentMethod,
        ship_to_contact_id: fulfillmentMethod === 'SHIP' ? selectedShipToContact?.id : null,
        // Pricing fields
        shipping_cost: parseFloat(shippingCost) || 0,
        // Discount - backend expects 'discount' field as flat amount
        discount: calculateDiscountAmount(),
        discount_description: discountDescription || null,
        // Store discount config for reference (type and original value)
        discount_type: discountType,
        discount_value: parseFloat(discountValue) || 0,
        // Order-level email for notifications
        customer_email: orderEmail || customerEmail || null,
        // Order summary
        order_summary: orderSummary || null,
        // Line items with separate item name and description
        items: lineItems.filter(item => item.item_name).map(item => ({
          product_name: item.item_name,
          description: item.description || null,
          quantity: parseInt(item.quantity) || 1,
          unit_price: parseFloat(item.unit_price) || 0,
        })),
      };

      const result = await adminAPI.createOrder(orderData);
      toast.success(`Order ${result.order_id} created successfully`);
      onOpenChange(false);
      onSuccess?.(result.order_id);
    } catch (err) {
      // Handle structured error for missing ship-to address
      if (err.response?.data?.detail?.error?.code === 'MISSING_SHIP_TO_ADDRESS') {
        const errorData = err.response.data.detail.error;
        setMissingAddressContact({
          contact_id: errorData.meta.contact_id,
          contact_name: errorData.meta.contact_name,
          contact_email: errorData.meta.contact_email,
        });
        setMissingAddressForm({
          address_line1: '',
          address_line2: '',
          city: '',
          state: '',
          postal_code: '',
          country: 'USA',
        });
        setShowMissingAddressModal(true);
        toast.error(`${errorData.meta.contact_name} needs a shipping address`);
      } else {
        // Handle other errors
        const errorMsg = err.response?.data?.detail?.error?.message 
          || err.response?.data?.detail 
          || err.message 
          || 'Failed to create order';
        toast.error(errorMsg);
      }
    } finally {
      setSubmitting(false);
    }
  };
  
  // Handle saving missing address and retrying order creation
  const handleSaveMissingAddress = async () => {
    if (!missingAddressContact?.contact_id) return;
    
    // Validate
    if (!missingAddressForm.address_line1.trim()) {
      toast.error('Address line 1 is required');
      return;
    }
    if (!missingAddressForm.city.trim()) {
      toast.error('City is required');
      return;
    }
    if (!missingAddressForm.state.trim()) {
      toast.error('State is required');
      return;
    }
    if (!missingAddressForm.postal_code.trim()) {
      toast.error('ZIP code is required');
      return;
    }
    
    setSavingMissingAddress(true);
    try {
      // Update the contact with the new shipping address
      await crmAPI.updateContact(missingAddressContact.contact_id, {
        shipping_address: missingAddressForm
      });
      
      toast.success(`Shipping address added to ${missingAddressContact.contact_name}`);
      
      // Close modal
      setShowMissingAddressModal(false);
      setMissingAddressContact(null);
      
      // Refresh ship-to contacts list
      if (selectedCustomer?.id) {
        await fetchShipToContacts(selectedCustomer.id);
      }
      
      // Inform user to retry
      toast.info('Address saved. Please click "Create Order" again.');
    } catch (err) {
      toast.error(err.message || 'Failed to save address');
    } finally {
      setSavingMissingAddress(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto" data-testid="create-order-modal">
        <DialogHeader>
          <DialogTitle>
            {prefillSourceOrders.length > 0 ? 'Create Order from Existing' : 'Create New Order'}
          </DialogTitle>
        </DialogHeader>

        {/* Prefill Banner - shown when copying from existing orders */}
        {prefillLoading && (
          <div className="flex items-center gap-2 p-3 bg-blue-50 border border-blue-200 rounded-lg text-blue-700">
            <Loader2 className="w-4 h-4 animate-spin" />
            <span className="text-sm">Loading order data...</span>
          </div>
        )}
        
        {prefillSourceOrders.length > 0 && !prefillLoading && (
          <div className="flex items-center gap-2 p-3 bg-amber-50 border border-amber-200 rounded-lg" data-testid="prefill-banner">
            <Copy className="w-4 h-4 text-amber-600 flex-shrink-0" />
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-amber-800">
                Items copied from: {prefillSourceOrders.join(', ')}
              </p>
              <p className="text-xs text-amber-600">
                {lineItems.length} item(s) loaded • Quantities combined • Prices preserved
              </p>
            </div>
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Top Row: Customer Selection + Order Email */}
          <div className="grid grid-cols-2 gap-6">
            {/* Customer Selection */}
            <div className="space-y-3">
              <Label className="text-sm font-medium">Customer *</Label>

              {selectedCustomer && !showCustomerPicker ? (
                <div className="flex items-center justify-between p-3 bg-ivory-50 border rounded-lg">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-lg bg-ivory-200 flex items-center justify-center">
                      <Building2 className="w-5 h-5 text-ivory-600" />
                    </div>
                    <div>
                      <p className="font-medium text-ivory-800">{customerName}</p>
                      {customerEmail && (
                        <p className="text-sm text-ivory-500">{customerEmail}</p>
                      )}
                    </div>
                  </div>
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={handleClearCustomer}
                  >
                    <X className="w-4 h-4" />
                  </Button>
                </div>
              ) : (
                <div className="space-y-2">
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-ivory-400" />
                    <Input
                      value={customerSearch}
                      onChange={(e) => setCustomerSearch(e.target.value)}
                      placeholder="Search existing customers..."
                      className="pl-10"
                      data-testid="customer-search-input"
                    />
                    {searchLoading && (
                      <Loader2 className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 animate-spin text-ivory-400" />
                    )}
                  </div>

                  {/* Search results */}
                  {searchResults.length > 0 && (
                    <div className="border rounded-lg max-h-48 overflow-y-auto">
                      {searchResults.map((customer) => (
                        <button
                          key={customer.id}
                          type="button"
                          onClick={() => handleSelectCustomer(customer)}
                          className="w-full flex items-center gap-3 p-3 hover:bg-ivory-50 text-left border-b last:border-b-0"
                          data-testid={`customer-option-${customer.id}`}
                      >
                        <Building2 className="w-4 h-4 text-ivory-400" />
                        <div>
                          <p className="font-medium text-sm">{customer.name || customer.company_name}</p>
                          <p className="text-xs text-ivory-500">{customer.email}</p>
                        </div>
                      </button>
                    ))}
                  </div>
                )}

                {/* Manual entry option */}
                <div className="text-xs text-ivory-500">
                  Or enter customer details manually:
                </div>
                <Input
                  value={customerName}
                  onChange={(e) => setCustomerName(e.target.value)}
                  placeholder="Customer name *"
                  data-testid="customer-name-input"
                />
                <div className="grid grid-cols-2 gap-2">
                  <Input
                    type="email"
                    value={customerEmail}
                    onChange={(e) => setCustomerEmail(e.target.value)}
                    placeholder="Email (optional)"
                    data-testid="customer-email-input"
                  />
                  <Input
                    value={customerPhone}
                    onChange={(e) => setCustomerPhone(e.target.value)}
                    placeholder="Phone (optional)"
                    data-testid="customer-phone-input"
                  />
                </div>
              </div>
            )}
          </div>
          
            {/* Order Email & Customer PO (Right Column) */}
            <div className="space-y-3">
              <div>
                <Label className="text-sm font-medium flex items-center gap-2">
                  <Mail className="w-4 h-4" />
                  Order Email
                </Label>
                <Input
                  type="email"
                  value={orderEmail}
                  onChange={(e) => setOrderEmail(e.target.value)}
                  placeholder="Email for order notifications..."
                  className="mt-1"
                  data-testid="order-email-input"
                />
                <p className="text-xs text-gray-500 mt-1">
                  Used for invoices, quotes, and shipping notifications
                </p>
              </div>
              
              <div>
                <Label className="text-sm font-medium">Customer PO/Reference</Label>
                <Input
                  value={customerPoNumber}
                  onChange={(e) => setCustomerPoNumber(e.target.value)}
                  placeholder="Customer PO or reference number..."
                  className="mt-1"
                  data-testid="customer-po-input"
                />
              </div>
            </div>
          </div>

          {/* Order Summary - Above Line Items */}
          <div className="space-y-2">
            <Label className="text-sm font-medium">Order Summary</Label>
            <Textarea
              value={orderSummary}
              onChange={(e) => setOrderSummary(e.target.value)}
              placeholder="Brief description of the order (e.g., '50 Crystal Awards for Annual Sales Meeting')..."
              rows={2}
              data-testid="order-summary-input"
            />
          </div>

          {/* Fulfillment Method & Ship-To Section - Show when customer is selected or name is provided */}
          {(selectedCustomer || customerName) && (
            <div className="space-y-4 border-t pt-4">
              {/* Fulfillment Method Selector */}
              <div className="space-y-2">
                <Label className="text-sm font-medium">Fulfillment Method</Label>
                <div className="flex gap-2">
                  <button
                    type="button"
                    onClick={() => setFulfillmentMethod('SHIP')}
                    className={`flex-1 flex items-center justify-center gap-2 p-3 rounded-lg border-2 transition-all ${
                      fulfillmentMethod === 'SHIP'
                        ? 'border-blue-500 bg-blue-50 text-blue-700'
                        : 'border-gray-200 hover:border-gray-300'
                    }`}
                    data-testid="fulfillment-ship"
                  >
                    <Truck className="w-4 h-4" />
                    <span className="font-medium">Ship</span>
                  </button>
                  <button
                    type="button"
                    onClick={() => setFulfillmentMethod('PICKUP')}
                    className={`flex-1 flex items-center justify-center gap-2 p-3 rounded-lg border-2 transition-all ${
                      fulfillmentMethod === 'PICKUP'
                        ? 'border-green-500 bg-green-50 text-green-700'
                        : 'border-gray-200 hover:border-gray-300'
                    }`}
                    data-testid="fulfillment-pickup"
                  >
                    <Store className="w-4 h-4" />
                    <span className="font-medium">Pickup</span>
                  </button>
                </div>
              </div>

              {/* Ship-To Section (only for SHIP) */}
              {fulfillmentMethod === 'SHIP' && selectedCustomer ? (
                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <Label className="text-sm font-medium">Ship To *</Label>
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() => setShowNewShipToModal(true)}
                      data-testid="new-ship-to-btn"
                    >
                      <Plus className="w-3 h-3 mr-1" />
                      New Ship-To Contact
                    </Button>
                  </div>

                  {loadingShipToContacts ? (
                    <div className="flex items-center justify-center p-4">
                      <Loader2 className="w-5 h-5 animate-spin text-gray-400" />
                    </div>
                  ) : shipToContacts.length > 0 ? (
                    <div className="space-y-2">
                      <Select
                        value={selectedShipToContact?.id || ''}
                        onValueChange={(value) => {
                          const contact = shipToContacts.find(c => c.id === value);
                          setSelectedShipToContact(contact);
                        }}
                      >
                        <SelectTrigger data-testid="ship-to-select">
                          <SelectValue placeholder="Select ship-to contact..." />
                        </SelectTrigger>
                        <SelectContent>
                          {shipToContacts.map((contact) => (
                            <SelectItem key={contact.id} value={contact.id}>
                              <div className="flex items-center gap-2">
                                {contact.is_default_ship_to && (
                                  <Star className="w-3 h-3 text-amber-500" />
                                )}
                                <span>
                                  {contact.first_name} {contact.last_name}
                                </span>
                                {contact.shipping_address && (
                                  <span className="text-xs text-gray-400">
                                    — {contact.shipping_address.city}, {contact.shipping_address.state}
                                  </span>
                                )}
                              </div>
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>

                      {/* Selected Contact Preview */}
                      {selectedShipToContact && (
                        <div className="bg-gray-50 rounded-lg p-3 space-y-2">
                          <div className="flex items-start justify-between">
                            <div className="flex items-center gap-2">
                              <MapPin className="w-4 h-4 text-gray-400" />
                              <span className="font-medium text-sm">
                                {selectedShipToContact.first_name} {selectedShipToContact.last_name}
                              </span>
                              {selectedShipToContact.is_default_ship_to && (
                                <Badge variant="secondary" className="text-xs">Default</Badge>
                              )}
                            </div>
                            {!selectedShipToContact.is_default_ship_to && (
                              <Button
                                type="button"
                                variant="ghost"
                                size="sm"
                                onClick={() => handleSetDefaultShipTo(selectedShipToContact)}
                                className="text-xs"
                              >
                                <Star className="w-3 h-3 mr-1" />
                                Set as Default
                              </Button>
                            )}
                          </div>
                          {selectedShipToContact.shipping_address && (
                            <div className="text-sm text-gray-600 pl-6">
                              <p>{selectedShipToContact.shipping_address.address_line1}</p>
                              {selectedShipToContact.shipping_address.address_line2 && (
                                <p>{selectedShipToContact.shipping_address.address_line2}</p>
                              )}
                              <p>
                                {selectedShipToContact.shipping_address.city}, {selectedShipToContact.shipping_address.state} {selectedShipToContact.shipping_address.postal_code}
                              </p>
                            </div>
                          )}
                          {selectedShipToContact.phone && (
                            <p className="text-sm text-gray-500 pl-6">{selectedShipToContact.phone}</p>
                          )}
                          {selectedShipToContact.delivery_notes && (
                            <p className="text-sm text-amber-600 pl-6 italic">
                              Note: {selectedShipToContact.delivery_notes}
                            </p>
                          )}
                        </div>
                      )}
                    </div>
                  ) : (
                    <div className="text-center p-4 bg-gray-50 rounded-lg">
                      <MapPin className="w-8 h-8 mx-auto text-gray-300 mb-2" />
                      <p className="text-sm text-gray-500">No ship-to contacts found</p>
                      <p className="text-xs text-gray-400">Create a new ship-to contact to continue</p>
                    </div>
                  )}
                </div>
              ) : fulfillmentMethod === 'SHIP' && !selectedCustomer ? (
                /* Manual shipping address for new customer */
                <div className="space-y-3">
                  <div className="flex items-center gap-2">
                    <MapPin className="w-4 h-4 text-gray-400" />
                    <Label className="text-sm font-medium">Shipping Address</Label>
                  </div>
                  <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-sm text-amber-700">
                    <AlertTriangle className="w-4 h-4 inline mr-2" />
                    To set up shipping, please select an existing customer or create a new customer first.
                  </div>
                </div>
              ) : fulfillmentMethod === 'PICKUP' ? (
                /* Pickup message */
                <div className="flex items-center gap-3 p-4 bg-green-50 rounded-lg border border-green-200">
                  <Store className="w-5 h-5 text-green-600" />
                  <div>
                    <p className="font-medium text-green-800">Customer Pickup</p>
                    <p className="text-sm text-green-600">No shipping address required.</p>
                  </div>
                </div>
              ) : null}
            </div>
          )}

          {/* Line Items (optional) */}
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <Label className="text-sm font-medium">Line Items</Label>
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={addLineItem}
                data-testid="add-line-item-btn"
              >
                <Plus className="w-3 h-3 mr-1" />
                Add Item
              </Button>
            </div>

            {lineItems.length > 0 && (
              <div className="border rounded-lg overflow-hidden">
                {/* Header row */}
                <div className="grid grid-cols-[1fr_70px_100px_90px_40px] gap-2 text-xs font-medium text-gray-500 uppercase px-3 py-2 bg-gray-50 border-b">
                  <span>Item</span>
                  <span className="text-center">Qty</span>
                  <span className="text-right">Unit Price</span>
                  <span className="text-right">Total</span>
                  <span></span>
                </div>
                
                {lineItems.map((item, idx) => (
                  <div key={item.id} className="border-b last:border-b-0">
                    {/* Row 1: Item name, Qty, Price, Total, Delete */}
                    <div className="grid grid-cols-[1fr_70px_100px_90px_40px] gap-2 items-center px-3 py-2">
                      <Input
                        value={item.item_name}
                        onChange={(e) => updateLineItem(item.id, 'item_name', e.target.value)}
                        placeholder="Item / Product name"
                        className="text-sm h-9"
                        data-testid={`line-item-name-${idx}`}
                      />
                      <Input
                        type="number"
                        value={item.quantity}
                        onChange={(e) => updateLineItem(item.id, 'quantity', e.target.value)}
                        placeholder="Qty"
                        min="1"
                        className="text-sm h-9 text-center"
                        data-testid={`line-item-qty-${idx}`}
                      />
                      <Input
                        type="number"
                        value={item.unit_price}
                        onChange={(e) => updateLineItem(item.id, 'unit_price', e.target.value)}
                        placeholder="0.00"
                        step="0.01"
                        min="0"
                        className="text-sm h-9 text-right"
                        data-testid={`line-item-price-${idx}`}
                      />
                      <div className="text-sm font-medium text-gray-700 text-right py-2">
                        ${((item.quantity || 0) * (item.unit_price || 0)).toFixed(2)}
                      </div>
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        onClick={() => removeLineItem(item.id)}
                        className="text-red-500 hover:text-red-700 p-1 h-8 w-8"
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                    
                    {/* Row 2: Multi-line description */}
                    <div className="px-3 pb-3">
                      <Textarea
                        value={item.description || ''}
                        onChange={(e) => updateLineItem(item.id, 'description', e.target.value)}
                        placeholder="Description / specifications (multi-line)..."
                        rows={2}
                        className="text-sm resize-none"
                        data-testid={`line-item-desc-${idx}`}
                      />
                    </div>
                  </div>
                ))}
              </div>
            )}

          </div>
          
          {/* Pricing: Shipping & Discount */}
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label className="text-sm font-medium">Shipping Cost</Label>
              <div className="relative">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500">$</span>
                <Input
                  type="number"
                  value={shippingCost}
                  onChange={(e) => setShippingCost(e.target.value)}
                  placeholder="0.00"
                  step="0.01"
                  min="0"
                  className="pl-7 text-sm"
                  data-testid="shipping-cost-input"
                />
              </div>
            </div>
            
            {/* Discount with Type Selector - Consistent with Order Detail */}
            <div className="space-y-2">
              <Label className="text-sm font-medium">Discount</Label>
              <div className="flex gap-2">
                {/* Type Toggle */}
                <div className="flex rounded-md border border-gray-200 overflow-hidden">
                  <button
                    type="button"
                    onClick={() => setDiscountType('flat')}
                    className={`px-3 py-2 text-sm flex items-center gap-1 transition-colors ${
                      discountType === 'flat' 
                        ? 'bg-gold-100 text-gold-700 font-medium' 
                        : 'bg-white text-gray-500 hover:bg-gray-50'
                    }`}
                    data-testid="discount-type-flat"
                  >
                    <DollarSign className="w-3 h-3" />
                  </button>
                  <button
                    type="button"
                    onClick={() => setDiscountType('percentage')}
                    className={`px-3 py-2 text-sm flex items-center gap-1 transition-colors border-l ${
                      discountType === 'percentage' 
                        ? 'bg-gold-100 text-gold-700 font-medium' 
                        : 'bg-white text-gray-500 hover:bg-gray-50'
                    }`}
                    data-testid="discount-type-percentage"
                  >
                    <Percent className="w-3 h-3" />
                  </button>
                </div>
                {/* Value Input */}
                <div className="relative flex-1">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500">
                    {discountType === 'flat' ? '$' : '%'}
                  </span>
                  <Input
                    type="number"
                    value={discountValue}
                    onChange={(e) => setDiscountValue(e.target.value)}
                    placeholder="0.00"
                    step="0.01"
                    min="0"
                    max={discountType === 'percentage' ? 100 : undefined}
                    className="pl-7 text-sm"
                    data-testid="discount-value-input"
                  />
                </div>
              </div>
              {/* Show calculated discount for percentage */}
              {discountType === 'percentage' && parseFloat(discountValue) > 0 && (
                <p className="text-xs text-gray-500">
                  = ${calculateDiscountAmount().toFixed(2)} off
                </p>
              )}
            </div>
          </div>
          
          {/* Discount Description */}
          {(parseFloat(discountValue) > 0) && (
            <div className="space-y-2">
              <Label className="text-sm font-medium">Discount Description (optional)</Label>
              <Input
                value={discountDescription}
                onChange={(e) => setDiscountDescription(e.target.value)}
                placeholder="e.g., 10% loyalty discount, Bulk order discount"
                className="text-sm"
                data-testid="discount-description-input"
              />
            </div>
          )}
          
          {/* Totals Summary */}
          {(lineItems.length > 0 || parseFloat(shippingCost) > 0 || calculateDiscountAmount() > 0) && (
            <div className="bg-gray-50 rounded-lg p-4 space-y-2">
              {calculateSubtotal() > 0 && (
                <div className="flex justify-between text-sm">
                  <span className="text-gray-600">Subtotal:</span>
                  <span className="font-medium">${calculateSubtotal().toFixed(2)}</span>
                </div>
              )}
              {parseFloat(shippingCost) > 0 && (
                <div className="flex justify-between text-sm">
                  <span className="text-gray-600">Shipping:</span>
                  <span className="font-medium">${parseFloat(shippingCost).toFixed(2)}</span>
                </div>
              )}
              {calculateDiscountAmount() > 0 && (
                <div className="flex justify-between text-sm">
                  <span className="text-gray-600">
                    Discount{discountDescription ? ` (${discountDescription})` : ''}{discountType === 'percentage' ? ` (${discountValue}%)` : ''}:
                  </span>
                  <span className="font-medium text-red-600">-${calculateDiscountAmount().toFixed(2)}</span>
                </div>
              )}
              <div className="flex justify-between text-sm font-semibold pt-2 border-t">
                <span>Total (excl. tax):</span>
                <span>${calculateTotal().toFixed(2)}</span>
              </div>
              <p className="text-xs text-gray-500">Tax will be calculated based on shipping address</p>
            </div>
          )}

          {/* Notes */}
          <div className="space-y-2">
            <Label className="text-sm font-medium">Notes (optional)</Label>
            <Textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Order notes..."
              rows={2}
              data-testid="order-notes-input"
            />
          </div>

          {/* Artwork Required Toggle */}
          <div className="flex items-center justify-between p-3 bg-ivory-50 rounded-lg">
            <div>
              <Label className="text-sm font-medium">Artwork Required</Label>
              <p className="text-xs text-ivory-500">Mark if this order needs artwork approval</p>
            </div>
            <Switch
              checked={artworkRequired}
              onCheckedChange={setArtworkRequired}
              data-testid="artwork-required-toggle"
            />
          </div>

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              disabled={submitting}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={submitting || !customerName.trim() || (fulfillmentMethod === 'SHIP' && selectedCustomer && !selectedShipToContact)}
              data-testid="submit-order-btn"
            >
              {submitting ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Creating...
                </>
              ) : (
                'Create Order'
              )}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>

      {/* New Ship-To Contact Modal */}
      <Dialog open={showNewShipToModal} onOpenChange={setShowNewShipToModal}>
        <DialogContent className="max-w-md" data-testid="new-ship-to-modal">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <MapPin className="w-5 h-5" />
              New Ship-To Contact
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            {/* Contact Name */}
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>First Name *</Label>
                <Input
                  value={newShipToForm.first_name}
                  onChange={(e) => setNewShipToForm(prev => ({ ...prev, first_name: e.target.value }))}
                  placeholder="First name"
                  data-testid="ship-to-first-name"
                />
              </div>
              <div>
                <Label>Last Name</Label>
                <Input
                  value={newShipToForm.last_name}
                  onChange={(e) => setNewShipToForm(prev => ({ ...prev, last_name: e.target.value }))}
                  placeholder="Last name"
                  data-testid="ship-to-last-name"
                />
              </div>
            </div>
            
            {/* Contact Info */}
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Email</Label>
                <Input
                  type="email"
                  value={newShipToForm.email}
                  onChange={(e) => setNewShipToForm(prev => ({ ...prev, email: e.target.value }))}
                  placeholder="email@example.com"
                />
              </div>
              <div>
                <Label>Phone</Label>
                <Input
                  value={newShipToForm.phone}
                  onChange={(e) => setNewShipToForm(prev => ({ ...prev, phone: e.target.value }))}
                  placeholder="(555) 123-4567"
                />
              </div>
            </div>
            
            {/* Shipping Address */}
            <div className="space-y-3 border-t pt-4">
              <Label className="text-sm font-medium">Shipping Address *</Label>
              <Input
                value={newShipToForm.shipping_address.address_line1}
                onChange={(e) => setNewShipToForm(prev => ({ 
                  ...prev, 
                  shipping_address: { ...prev.shipping_address, address_line1: e.target.value }
                }))}
                placeholder="Street address *"
                data-testid="ship-to-address1"
              />
              <Input
                value={newShipToForm.shipping_address.address_line2}
                onChange={(e) => setNewShipToForm(prev => ({ 
                  ...prev, 
                  shipping_address: { ...prev.shipping_address, address_line2: e.target.value }
                }))}
                placeholder="Apt, suite, etc. (optional)"
              />
              <div className="grid grid-cols-3 gap-2">
                <Input
                  value={newShipToForm.shipping_address.city}
                  onChange={(e) => setNewShipToForm(prev => ({ 
                    ...prev, 
                    shipping_address: { ...prev.shipping_address, city: e.target.value }
                  }))}
                  placeholder="City *"
                  data-testid="ship-to-city"
                />
                <Input
                  value={newShipToForm.shipping_address.state}
                  onChange={(e) => setNewShipToForm(prev => ({ 
                    ...prev, 
                    shipping_address: { ...prev.shipping_address, state: e.target.value.toUpperCase().slice(0, 2) }
                  }))}
                  placeholder="State *"
                  maxLength={2}
                  data-testid="ship-to-state"
                />
                <Input
                  value={newShipToForm.shipping_address.postal_code}
                  onChange={(e) => setNewShipToForm(prev => ({ 
                    ...prev, 
                    shipping_address: { ...prev.shipping_address, postal_code: e.target.value }
                  }))}
                  placeholder="ZIP *"
                  data-testid="ship-to-zip"
                />
              </div>
            </div>
            
            {/* Delivery Notes */}
            <div>
              <Label>Delivery Notes</Label>
              <Textarea
                value={newShipToForm.delivery_notes}
                onChange={(e) => setNewShipToForm(prev => ({ ...prev, delivery_notes: e.target.value }))}
                placeholder="Gate code, special instructions..."
                rows={2}
              />
            </div>
            
            {/* Set as Default */}
            <div className="flex items-center gap-2 p-3 bg-gray-50 rounded-lg">
              <input
                type="checkbox"
                id="default-ship-to"
                checked={newShipToForm.is_default_ship_to}
                onChange={(e) => setNewShipToForm(prev => ({ ...prev, is_default_ship_to: e.target.checked }))}
                className="rounded"
              />
              <label htmlFor="default-ship-to" className="text-sm cursor-pointer">
                Set as default ship-to for this customer
              </label>
              {shipToContacts.some(c => c.is_default_ship_to) && newShipToForm.is_default_ship_to && (
                <AlertTriangle className="w-4 h-4 text-amber-500 ml-auto" title="Will replace existing default" />
              )}
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowNewShipToModal(false)}>
              Cancel
            </Button>
            <Button
              onClick={handleCreateShipToContact}
              disabled={savingShipTo}
              data-testid="save-ship-to-btn"
            >
              {savingShipTo ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
              Create Contact
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Default Change Warning Modal */}
      <AlertDialog open={showDefaultChangeWarning} onOpenChange={setShowDefaultChangeWarning}>
        <AlertDialogContent data-testid="default-change-warning">
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2">
              <AlertTriangle className="w-5 h-5 text-amber-500" />
              Change Default Ship-To?
            </AlertDialogTitle>
            <AlertDialogDescription className="space-y-2">
              <p>This will change the default ship-to contact for this customer.</p>
              <div className="bg-gray-50 rounded p-3 text-sm">
                <p className="text-gray-600">
                  <strong>Current default:</strong>{' '}
                  {shipToContacts.find(c => c.is_default_ship_to)?.first_name}{' '}
                  {shipToContacts.find(c => c.is_default_ship_to)?.last_name}
                  {shipToContacts.find(c => c.is_default_ship_to)?.shipping_address && (
                    <span className="text-gray-400">
                      {' — '}
                      {shipToContacts.find(c => c.is_default_ship_to)?.shipping_address.city},{' '}
                      {shipToContacts.find(c => c.is_default_ship_to)?.shipping_address.state}
                    </span>
                  )}
                </p>
                <p className="text-blue-600 mt-1">
                  <strong>New default:</strong>{' '}
                  {pendingDefaultContact?.isNew ? (
                    <>
                      {pendingDefaultContact.first_name} {pendingDefaultContact.last_name}
                      {pendingDefaultContact.shipping_address?.city && (
                        <span className="text-blue-400">
                          {' — '}{pendingDefaultContact.shipping_address.city}, {pendingDefaultContact.shipping_address.state}
                        </span>
                      )}
                    </>
                  ) : (
                    <>
                      {pendingDefaultContact?.first_name} {pendingDefaultContact?.last_name}
                      {pendingDefaultContact?.shipping_address && (
                        <span className="text-blue-400">
                          {' — '}{pendingDefaultContact.shipping_address.city}, {pendingDefaultContact.shipping_address.state}
                        </span>
                      )}
                    </>
                  )}
                </p>
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                if (pendingDefaultContact?.isNew) {
                  saveNewShipToContact();
                } else if (pendingDefaultContact) {
                  confirmSetDefaultShipTo(pendingDefaultContact);
                }
              }}
            >
              Confirm Change
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
      
      {/* Missing Address Fix Modal */}
      <Dialog open={showMissingAddressModal} onOpenChange={setShowMissingAddressModal}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <AlertTriangle className="w-5 h-5 text-amber-500" />
              Add Shipping Address
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <p className="text-sm text-gray-600">
              <strong>{missingAddressContact?.contact_name}</strong> doesn&apos;t have a shipping address. 
              Add one to continue creating this order.
            </p>
            
            <div className="space-y-3">
              <div>
                <Label>Address Line 1 *</Label>
                <Input
                  value={missingAddressForm.address_line1}
                  onChange={(e) => setMissingAddressForm(f => ({ ...f, address_line1: e.target.value }))}
                  placeholder="123 Main St"
                  data-testid="missing-address-line1"
                />
              </div>
              <div>
                <Label>Address Line 2</Label>
                <Input
                  value={missingAddressForm.address_line2}
                  onChange={(e) => setMissingAddressForm(f => ({ ...f, address_line2: e.target.value }))}
                  placeholder="Suite 100"
                />
              </div>
              <div className="grid grid-cols-3 gap-2">
                <div>
                  <Label>City *</Label>
                  <Input
                    value={missingAddressForm.city}
                    onChange={(e) => setMissingAddressForm(f => ({ ...f, city: e.target.value }))}
                    placeholder="Los Angeles"
                    data-testid="missing-address-city"
                  />
                </div>
                <div>
                  <Label>State *</Label>
                  <Input
                    value={missingAddressForm.state}
                    onChange={(e) => setMissingAddressForm(f => ({ ...f, state: e.target.value }))}
                    placeholder="CA"
                    data-testid="missing-address-state"
                  />
                </div>
                <div>
                  <Label>ZIP *</Label>
                  <Input
                    value={missingAddressForm.postal_code}
                    onChange={(e) => setMissingAddressForm(f => ({ ...f, postal_code: e.target.value }))}
                    placeholder="90001"
                    data-testid="missing-address-zip"
                  />
                </div>
              </div>
            </div>
          </div>
          
          <DialogFooter>
            <Button 
              variant="outline" 
              onClick={() => setShowMissingAddressModal(false)}
              disabled={savingMissingAddress}
            >
              Cancel
            </Button>
            <Button 
              onClick={handleSaveMissingAddress}
              disabled={savingMissingAddress}
              data-testid="save-missing-address-btn"
            >
              {savingMissingAddress && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              Save & Retry
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Dialog>
  );
};

export default CreateOrderModal;
